import {PlayCircle} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  CircularProgress,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import dayjs from 'dayjs';
import update from 'immutability-helper';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {toDisplaySpeed} from '../../../constants/common';
import {useAppSelector} from '../../../hooks/redux';
import {useRefreshInterval} from '../../../hooks/refreshInterval';
import {ExportField} from '../../../interfaces/Export';
import {
  HazardEventLog,
  HazardEventLogListQuery,
  HazardEventLogListResponse,
} from '../../../interfaces/HazardAILog';
import {SafeyeNode} from '../../../interfaces/SafeyeNode';
import AccessControl from '../../common/AccessControl';
import {getHasPermission} from '../../common/AccessControl/utils';
import {AutoRefreshSelect} from '../../common/AutoRefreshSelect';
import DataGrid, {DataGridColumn, DataGridRef} from '../../common/DataGrid';
import {HazardMap, MapLayerId, MapType} from '../../common/HazardMap';
import {ResizableColumns} from '../../common/ResizableColumns';
import {DateRangeSelect} from '../../selectors/DateRangeSelect';
import {SafeyeNodeSelector} from '../../selectors/SafeyeNodeSelector';
import {DashboardPanelTitleSlot} from '../DashboardPanelTitleSlot';
import {HeatmapObjectSelector} from '../HazardHeatmap/HeatmapObjectSelector';
import {HeatmapZoneSelector} from '../HazardHeatmap/HeatmapZoneSelector';
import HazardAIDetectionEditButton from './HazardAIDetectionEditButton';
import HazardAILogExportButton from './HazardAILogExportButton';

interface Props {
  value?: HazardDetectionAIReportData;
  hasConnectView?: boolean;
  onUpdate?: (value?: HazardDetectionAIReportData) => void;
  onClickMedia?: (id?: number) => void;
}

const DEFAULT_SHOWN_FIELDS = [
  'id',
  'timestamp',
  'zone',
  'object',
  'confidence',
  'raw_image',
  'distance',
  'cam_id',
  'speed',
  'latitude',
  'longitude',
  'machine_name',
];

export interface HazardDectionAILogReportDataTab {
  id: number;
  selectedMachineId: number | null;
  params: Partial<HazardEventLogListQuery>;
  selectedIds: number[] | null;
  selectAll: boolean;
  shownFields: string[];
  exportFields: ExportField[];
  refreshInterval?: number | null;
  columnWidths: Record<string, number>;
  columnOrder?: string[];
}

export interface HazardDetectionAIReportData {
  activeId?: number;
  openedItems: HazardDectionAILogReportDataTab[];
  mapLayers: MapLayerId[];
  mapLevel: number | null;
  mapType?: MapType;
}

export const getHazardDectionAIReportData = (): HazardDetectionAIReportData => {
  return {
    activeId: undefined,
    openedItems: [],
    mapLayers: ['hazardAiLogs'],
    mapLevel: 2,
  };
};

export const getHazardDectionAIReportDataTab = (
  id: number
): HazardDectionAILogReportDataTab => {
  return {
    id,
    selectAll: true,
    selectedMachineId: null,
    params: {
      date_start: dayjs().format('YYYY-MM-DD'),
      date_end: dayjs().format('YYYY-MM-DD'),
      limit: 25,
      page: 0,
    },
    shownFields: DEFAULT_SHOWN_FIELDS,
    selectedIds: null,
    exportFields: [],
    columnWidths: {},
    columnOrder: undefined,
  };
};

const HazardAIDetectionLogReport = ({
  value,
  hasConnectView,
  onUpdate,
  onClickMedia,
}: Props) => {
  const me = useAppSelector(({app}) => app.me);
  const speedFormat = useAppSelector(({app}) => app.speedFormat);
  const safeyeNodes = useAppSelector(({assets}) => assets.safeye_nodes);

  const config = useMemo(() => {
    if (hasConnectView) {
      const v = value ?? getHazardDectionAIReportData();
      return {
        ...v,
        mapLayers: v.mapLayers ?? ['hazardAiLogs'],
        mapLevel: v.mapLevel ?? null,
      };
    } else {
      const v = value ?? getHazardDectionAIReportData();
      if (!value?.activeId) {
        return {
          ...v,
          openedItems: [],
          mapLayers: v.mapLayers ?? ['heatmap', 'street'],
          mapLevel: v.mapLevel ?? null,
        };
      } else {
        return {
          ...v,
          mapLayers: v.mapLayers ?? ['heatmap', 'street'],
          mapLevel: v.mapLevel ?? null,
        };
      }
    }
  }, [value, hasConnectView]);

  const openedItemIndex = useMemo(
    () =>
      (config.activeId
        ? config.openedItems.findIndex((i) => i.id === config.activeId)
        : null) ?? config.openedItems.length - 1,
    [config.activeId, config.openedItems]
  );

  const openedItem = useMemo(
    () =>
      openedItemIndex !== -1
        ? config.openedItems[openedItemIndex] &&
          config.openedItems[openedItemIndex].params
          ? config.openedItems[openedItemIndex]
          : getHazardDectionAIReportDataTab(config.activeId as number)
        : null,
    [openedItemIndex, config.openedItems]
  );

  const tabNames = useMemo(
    () =>
      config.openedItems.map((o) => {
        const machine: SafeyeNode | null =
          safeyeNodes?.find((it) => `${it.id}` === `${o.id}`) ?? null;
        return machine?.asset_name
          ? machine?.asset_name
          : `Machine Not Found (Summary ID: #${o?.id})`;
      }),
    [config.openedItems, safeyeNodes]
  );

  const [fetchedDetectionLog, setFetchedDetectionLog] =
    useState<HazardEventLogListResponse>();
  const [fetchedDetectionLogErrors, setFetchedDetectionLogErrors] = useState<
    string[]
  >([]);
  const [fetchedDetectionLogInProgress, setFetchedDetectionLogInProgress] =
    useState(false);

  const params = useMemo<HazardEventLogListQuery | null>(
    () =>
      openedItem?.id
        ? {
            id: openedItem?.id,
            date_start:
              openedItem.params?.date_start ?? dayjs().format('YYYY-MM-DD'),
            date_end:
              openedItem.params?.date_end ?? dayjs().format('YYYY-MM-DD'),
            page: openedItem.params?.page ?? 0,
            limit: openedItem.params?.limit ?? 25,
            order: openedItem.params?.order ?? 'id',
            dir: openedItem.params?.dir ?? 'DESC',
            zone: openedItem.params?.zone,
            object: openedItem.params?.object,
          }
        : null,
    [openedItem?.id, openedItem?.params]
  );
  const fetchDetectionLogs = useCallback(
    async (params: HazardEventLogListQuery) => {
      if (params?.id) {
        setFetchedDetectionLogInProgress(true);
        setFetchedDetectionLogErrors([]);

        try {
          const resp = await API.get<HazardEventLogListResponse>(
            `${apiBaseUrl}/hazard-ai/${params?.id}/detection-event-log`,
            {params}
          );

          setFetchedDetectionLog(resp.data);
          if (openedItem && openedItem.selectAll) {
            openedItem.selectedIds = resp.data.items.map((el) => el.id);
          }
        } catch (error: any) {
          const messages = getMessagesFromApiError(error, 'STRA_2aecd54d');
          setFetchedDetectionLogErrors(messages);
        } finally {
          setFetchedDetectionLogInProgress(false);
        }
      }
    },
    [params]
  );

  useEffect(() => {
    if (params) {
      fetchDetectionLogs(params);
    }
  }, [params]);

  useEffect(() => {
    if (!openedItem) {
      setFetchedDetectionLog(undefined);
    }
  }, [openedItem]);

  /****************/
  /* auto refresh */
  /****************/
  const callFetchData = useCallback(() => {
    params && fetchDetectionLogs(params);
  }, [params]);

  useRefreshInterval(callFetchData, openedItem?.refreshInterval);

  /*************/
  /* data grid */
  /*************/
  const dataGridRef = useRef<DataGridRef>(null);
  const rows = fetchedDetectionLog?.items ?? [];
  const columns: DataGridColumn<HazardEventLog>[] = [
    {
      field: 'select',
      type: 'select',
      width: 40,
      doNotExport: true,
      renderHeader: () => (
        <Checkbox
          color="primary"
          disabled={rows.length === 0}
          checked={selectedItems.length > 0 && selectedAll}
          indeterminate={selectedItems.length > 0 && !selectedAll}
          onChange={() => toggleSelectAllItems()}
        />
      ),
      renderCell: ({row}) => (
        <Checkbox
          color="primary"
          checked={selectedItems.includes(row.id)}
          onChange={() => toggleSelectItem(row.id)}
        />
      ),
    },
    ...(getHasPermission(me?.permissions ?? [], [
      'patch::/hazard-ai/:id(\\d+)/false-detection',
    ])
      ? [
          {
            field: 'actions',
            renderHeader: () => (
              <Tooltip title="Edit">
                <MoreHorizIcon />
              </Tooltip>
            ),
            type: 'actions',
            doNotExport: true,
            width: 40,
            sxHeader: {textAlign: 'center', p: 0.5},
            sxCell: {textAlign: 'center', p: 0.5},
            renderCell: ({row}) => {
              return (
                <>
                  <AccessControl
                    permissions={[
                      'patch::/hazard-ai/:id(\\d+)/false-detection',
                    ]}
                  >
                    <HazardAIDetectionEditButton
                      component={IconButton}
                      componentProps={{
                        color: 'primary',
                      }}
                      item={row}
                      onDone={callFetchData}
                    >
                      <MoreHorizIcon />
                    </HazardAIDetectionEditButton>
                  </AccessControl>
                </>
              );
            },
          } as DataGridColumn<HazardEventLog>,
        ]
      : []),
    {
      field: 'type',
      width: 40,
      headerName: 'Detection Status',
      resizeable: true,
      sortable: true,
      renderHeader: () => {
        return (
          <Tooltip title="Detection Status">
            <FiberManualRecordIcon />
          </Tooltip>
        );
      },
      renderCell: ({row}) => {
        return row.type === 'detection' ? (
          <Tooltip title="Valid Detection">
            <FiberManualRecordIcon color="success" />
          </Tooltip>
        ) : (
          <Tooltip title="False Detection">
            <FiberManualRecordIcon color="error" />
          </Tooltip>
        );
      },
    },
    {
      field: 'timestamp',
      headerName: 'Timestamp',
      width: 160,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'zone',
      headerName: 'Zone',
      width: 120,
      resizeable: true,
      sortable: true,
      renderCell: ({row}) => {
        return <Box textTransform="capitalize">{row.zone}</Box>;
      },
    },
    {
      field: 'object',
      headerName: 'Object',
      width: 120,
      resizeable: true,
      sortable: true,
      renderCell: ({row}) => {
        return <Box textTransform="capitalize">{row.object}</Box>;
      },
    },
    {
      field: 'confidence',
      headerName: 'Detection Confidence',
      width: 180,
      resizeable: true,
      sortable: true,
      renderCell: ({row}) => {
        return <Box textTransform="capitalize">{row.confidence}</Box>;
      },
    },
    {
      field: 'raw_image',
      headerName: 'Detection Video',
      width: 140,
      resizeable: true,
      sortable: true,
      doNotExport: true,
      renderCell: ({row}) => {
        return (
          <Box width="100%" display="flex" justifyContent="center">
            <IconButton
              onClick={() => {
                onClickMedia?.(row.id);
              }}
            >
              <PlayCircle />
            </IconButton>
          </Box>
        );
      },
    },
    {
      field: 'distance',
      headerName: 'Detection distance',
      width: 160,
      resizeable: true,
      sortable: true,
      renderCell: ({row}) => {
        return row.distance;
      },
    },
    {
      field: 'cam_id',
      headerName: 'Camera Position',
      width: 150,
      resizeable: true,
      sortable: true,
      renderCell: ({row}) => {
        return row.cam_id;
      },
    },
    {
      field: 'speed',
      headerName: `Speed (${speedFormat})`,
      width: 80,
      resizeable: true,
      sortable: true,
      renderCell: ({row}) =>
        row.speed != null ? toDisplaySpeed(Number(row.speed), speedFormat) : null,
    },
    {
      field: 'latitude',
      headerName: 'Latitude',
      width: 120,
      resizeable: true,
      sortable: true,
      renderCell: ({row}) => {
        return row.latitude;
      },
    },
    {
      field: 'longitude',
      headerName: 'Longitude',
      width: 120,
      resizeable: true,
      sortable: true,
      renderCell: ({row}) => {
        return row.longitude;
      },
    },
  ];

  const shownFields = useMemo(() => {
    return openedItem?.shownFields ?? DEFAULT_SHOWN_FIELDS;
  }, [openedItem]);

  useEffect(() => {
    if (openedItemIndex > -1 && openedItem) {
      onUpdate?.(
        update(config, {
          openedItems: {
            [openedItemIndex]: {
              $set: {
                ...openedItem,
                exportFields: columns
                  .filter((col) => !col.doNotExport)
                  .map((col) => ({
                    field: col.field,
                    label: col.headerName,
                    hidden: shownFields?.indexOf(col.field) === -1,
                  })),
              },
            },
          },
        })
      );
    }
  }, [shownFields, openedItemIndex]);
  /*******************/
  /* multiple select */
  /*******************/
  const selectedItems = useMemo(() => {
    return openedItem?.selectedIds ?? [];
  }, [openedItem]);

  const selectedRows = useMemo(
    () => rows.filter((i) => openedItem?.selectedIds?.includes(i.id)),
    [rows, selectedItems]
  );

  const selectedAll = useMemo(
    () => rows.length === selectedRows.length,
    [rows, selectedRows]
  );

  const toggleSelectItem = (id: number) => {
    if (openedItem) {
      if (selectedItems?.includes(id)) {
        onUpdate?.(
          update(config, {
            openedItems: {
              [openedItemIndex]: {
                $set: {
                  ...openedItem,
                  selectedIds: selectedItems.filter((i) => i !== id),
                  selectAll: false,
                },
              },
            },
          })
        );
      } else {
        onUpdate?.(
          update(config, {
            openedItems: {
              [openedItemIndex]: {
                $set: {
                  ...openedItem,
                  selectedIds: [...(selectedItems ?? []), id],
                  selectAll:
                    selectedItems.length + 1 ===
                    fetchedDetectionLog?.items.length,
                },
              },
            },
          })
        );
      }
    }
  };

  const selectAll = () => {
    if (openedItem) {
      onUpdate?.(
        update(config, {
          openedItems: {
            [openedItemIndex]: {
              selectAll: {
                $set: true,
              },
              selectedIds: {
                $set: rows?.map((i) => i.id) ?? [],
              },
            },
          },
        })
      );
    }
  };

  const unselectAll = () => {
    if (openedItem) {
      onUpdate?.(
        update(config, {
          openedItems: {
            [openedItemIndex]: {
              selectAll: {
                $set: false,
              },
              selectedIds: {
                $set: [],
              },
            },
          },
        })
      );
    }
  };

  const toggleSelectAllItems = () => {
    if (selectedItems.length >= rows.length) {
      unselectAll();
    } else {
      selectAll();
    }
  };

  useEffect(() => {
    if (rows?.length > 0 && openedItem?.selectAll) {
      selectAll();
    }
  }, [rows]);

  const handleChangeShownFields = (fields: string[]) => {
    onUpdate?.(
      update(config, {
        openedItems: {
          [openedItemIndex]: {
            shownFields: {$set: fields},
          },
        },
      })
    );
  };

  const handleSafeyeNodeChange = (v: any) => {
    const openedItem: HazardDectionAILogReportDataTab =
      getHazardDectionAIReportDataTab(v);
    if (
      config.openedItems?.findIndex((it) => (it?.id ?? -1) === (v ?? -1)) > -1
    ) {
      onUpdate?.(
        update(config, {
          activeId: {
            $set: v,
          },
          openedItems: {
            $set: [...config.openedItems],
          },
        })
      );
    } else {
      onUpdate?.(
        update(config, {
          activeId: {
            $set: v,
          },
          openedItems: {
            $set: [...config.openedItems, openedItem],
          },
        })
      );
    }
    // }
  };

  /************/
  /* map data */
  /************/
  const mapData = useMemo(() => {
    if (fetchedDetectionLog?.items && openedItem?.selectedIds?.length) {
      return fetchedDetectionLog?.items.filter((i) =>
        openedItem?.selectedIds?.includes(i.id)
      );
    }
    return [];
  }, [fetchedDetectionLog, openedItem?.selectedIds, selectedItems]);

  const handleColumnWidthChange = (widths: Record<string, number>) => {
    onUpdate?.(
      update(config, {
        openedItems: {
          [openedItemIndex]: {
            columnWidths: {$set: widths},
          },
        },
      })
    );
  };

  const handleColumnOrderAndShownFieldsChange = useCallback(
    (order: string[], fields: string[]) => {
      if (openedItem) {
        onUpdate?.(
          update(config, {
            openedItems: {
              [openedItemIndex]: {
                columnOrder: {$set: order},
                shownFields: {$set: fields},
              },
            },
          })
        );
      }
    },
    [openedItem, openedItemIndex, config, onUpdate]
  );

  return (
    <Box
      height="100%"
      width="100%"
      display="flex"
      flexDirection="column"
      minHeight={0}
    >
      <DashboardPanelTitleSlot>Hazard AI Detection Log</DashboardPanelTitleSlot>
      {!hasConnectView && (
        <Box display="flex" flexDirection="column" flexGrow={1} p={2}>
          <SafeyeNodeSelector
            size="small"
            allOptionDisabled
            value={config?.activeId}
            onChange={handleSafeyeNodeChange}
          />
        </Box>
      )}
      <>
        {config.openedItems.length ? (
          <Box px={2} mb={2}>
            <Tabs
              value={openedItem?.id}
              variant="scrollable"
              onChange={(_event, v) => {
                if (v) {
                  onUpdate?.(
                    update(config, {
                      activeId: {
                        $set: v,
                      },
                    })
                  );
                }
              }}
            >
              {config.openedItems.map((i, idx) => (
                <Tab
                  key={i.id}
                  value={i.id}
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box>{tabNames?.[idx] ?? 'Undefined'}</Box>
                      <CloseIcon
                        fontSize="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          onUpdate?.(
                            update(value, {
                              activeId: {
                                $set:
                                  config.activeId === i.id
                                    ? undefined
                                    : config.activeId,
                              },
                              openedItems: {
                                $set:
                                  config.openedItems.filter(
                                    (o) => o.id && o.id !== i.id
                                  ) ?? [],
                              },
                            })
                          );
                        }}
                      />
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>
        ) : (
          <Box minWidth={400} px={1.5}>
            <Alert severity="warning">No Machine Selected</Alert>
          </Box>
        )}
      </>
      {fetchedDetectionLogErrors.map((error, idx) => (
        <Alert
          key={idx}
          severity="error"
          onClose={() => params && fetchDetectionLogs(params)}
        >
          {error}
        </Alert>
      ))}
      <Box
        overflow="auto"
        height={70}
        minHeight="70px"
        sx={{
          overflowY: 'hidden',
        }}
      >
        <Box
          p={2}
          minWidth={550}
          height={70}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          <Box display="flex" height="100%" marginLeft="auto">
            <Box minWidth={200}>
              <HeatmapZoneSelector
                size="small"
                fullWidth
                value={openedItem?.params?.zone}
                onChange={(v) => {
                  if (openedItem?.params) {
                    onUpdate?.(
                      update(config, {
                        openedItems: {
                          [openedItemIndex]: {
                            params: {
                              $set: {
                                ...openedItem?.params,
                                zone: v ?? undefined,
                              },
                            },
                          },
                        },
                      })
                    );
                  }
                }}
              ></HeatmapZoneSelector>
            </Box>
          </Box>
          <Box display="flex" height="100%">
            <Box minWidth={200}>
              <HeatmapObjectSelector
                size="small"
                fullWidth
                value={openedItem?.params?.object}
                onChange={(v) => {
                  if (openedItem?.params) {
                    onUpdate?.(
                      update(config, {
                        openedItems: {
                          [openedItemIndex]: {
                            params: {
                              $set: {
                                ...openedItem?.params,
                                object: v ?? undefined,
                              },
                            },
                          },
                        },
                      })
                    );
                  }
                }}
              ></HeatmapObjectSelector>
            </Box>
          </Box>
          <Box display="flex" height="100%">
            <Box minWidth={400}>
              <DateRangeSelect
                value={[
                  dayjs(openedItem?.params.date_start).toDate(),
                  dayjs(openedItem?.params.date_end).toDate(),
                ]}
                size="small"
                onChange={(v) => {
                  openedItem &&
                    onUpdate?.(
                      update(config, {
                        openedItems: {
                          [openedItemIndex]: {
                            params: {
                              $set: {
                                ...openedItem?.params,
                                date_start: v?.[0]
                                  ? dayjs(v?.[0]).format('YYYY-MM-DD')
                                  : undefined,
                                date_end: v?.[1]
                                  ? dayjs(v?.[1]).format('YYYY-MM-DD')
                                  : undefined,
                              },
                            },
                          },
                        },
                      })
                    );
                }}
              />
            </Box>
          </Box>

          <Box display="flex" height="100%">
            <ButtonGroup>
              <HazardAILogExportButton
                value={config.openedItems[openedItemIndex]}
                component={Button}
                componentProps={{color: 'primary'}}
                onSubmitted={() => {
                  if (params) {
                    fetchDetectionLogs?.(params);
                  }
                }}
              >
                <ImportExportIcon />
              </HazardAILogExportButton>
              <Button
                onClick={() => {
                  if (params) {
                    fetchDetectionLogs?.(params);
                  }
                }}
              >
                <RefreshIcon />
              </Button>
              <AutoRefreshSelect
                value={openedItem?.refreshInterval ?? null}
                onChange={(v) => {
                  if (openedItem) {
                    onUpdate?.(
                      update(config, {
                        openedItems: {
                          [openedItemIndex]: {
                            $set: {
                              ...openedItem,
                              refreshInterval: v,
                            },
                          },
                        },
                      })
                    );
                  }
                }}
              />

              <Button onClick={() => dataGridRef.current?.printTable()}>
                <PrintIcon />
              </Button>
            </ButtonGroup>
          </Box>
        </Box>
        {/* </Box> */}
      </Box>
      <ResizableColumns
        //position="relative"
        left={
          <HazardMap
            panel="detection_log_report"
            hazardDetectionLogs={mapData}
            selectedMapLayers={['hazardAiLogs']}
            selectedLevel={config.mapLevel}
            hazardMapType={config.mapType}
            minWidth={200}
            availableMapLayers={['hazardAiLogs']}
            onSelectLevel={(v) => {
              onUpdate?.(
                update(config, {
                  mapLevel: {
                    $set: v,
                  },
                })
              );
            }}
            onSelectMapLayers={(v) => {
              onUpdate?.(
                update(config, {
                  mapLayers: {
                    $set: v,
                  },
                })
              );
            }}
            onUpdateMapType={(v) => {
              onUpdate?.(
                update(config, {
                  mapType: {
                    $set: v,
                  },
                })
              );
            }}
            onClickMedia={(id) => {
              onClickMedia?.(id);
            }}
          />
        }
      >
        <Box display="flex" flexDirection="column" height="100%">
          <DataGrid
            ref={dataGridRef}
            rows={rows}
            columns={columns}
            size="small"
            pagination
            paginationMode="server"
            page={openedItem?.params?.page ?? 0}
            pageSize={openedItem?.params?.limit ?? 0}
            rowCount={fetchedDetectionLog?.count}
            shownFields={shownFields}
            defaultShownFields={DEFAULT_SHOWN_FIELDS}
            columnWidths={openedItem?.columnWidths}
            columnOrder={openedItem?.columnOrder}
            loading={fetchedDetectionLogInProgress}
            sx={{
              '.MuiTableContainer-root': {
                minHeight: '40px',
              },
            }}
            sxFooter={{
              bgcolor: (theme) =>
                theme.palette.mode === 'dark' ? '#2E2E2E' : '#FFF',
            }}
            footerStart={
              selectedItems.length ? (
                <Box display="flex" alignItems="center" gap={3} px={1}>
                  <Box
                    display="flex"
                    gap={0.5}
                    alignItems="center"
                    height="100%"
                    whiteSpace="nowrap"
                  >
                    {selectedItems.length} selected
                  </Box>
                </Box>
              ) : null
            }
            onShownFieldsChange={handleChangeShownFields}
            onColumnWidthsChange={handleColumnWidthChange}
            onColumnOrderAndShownFieldsChange={handleColumnOrderAndShownFieldsChange}
            onPageChange={(v) => {
              openedItem &&
                onUpdate?.(
                  update(config, {
                    openedItems: {
                      [openedItemIndex]: {
                        params: {
                          $set: {
                            ...openedItem?.params,
                            page: v ?? 0,
                          },
                        },
                      },
                    },
                  })
                );
            }}
            onPageSizeChange={(v) => {
              openedItem &&
                onUpdate?.(
                  update(config, {
                    openedItems: {
                      [openedItemIndex]: {
                        params: {
                          $set: {
                            ...openedItem?.params,
                            page: 0,
                            limit: v,
                          },
                        },
                      },
                    },
                  })
                );
            }}
          />
        </Box>
        <Backdrop
          open={fetchedDetectionLogInProgress}
          sx={{position: 'absolute', zIndex: 1199}}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </ResizableColumns>
    </Box>
  );
};

export default HazardAIDetectionLogReport;
